export const tools = [
    {
        name: 'engagement_Consent_list',
        description: "The tenant's consent ledger: the current state per subject and channel.",
        context: 'engagement',
        operationId: 'Consent_list',
        method: 'GET',
        path: '/consent',
        action: 'consent:list',
        auth: 'tenant',
        inputSchema: {
            type: 'object',
            properties: {
                query: {
                    type: 'object',
                    properties: {
                        cursor: {
                            type: 'string',
                        },
                        limit: {
                            type: 'integer',
                            format: 'int32',
                            minimum: 1,
                            maximum: 200,
                            default: 50,
                        },
                        subject: {
                            type: 'string',
                        },
                    },
                    required: [],
                    additionalProperties: false,
                },
            },
            required: [],
            additionalProperties: false,
        },
    },
    {
        name: 'engagement_Consent_add',
        description: 'Record an opt-in or opt-out by hand (a verbal request, a written one). Appends to the history.',
        context: 'engagement',
        operationId: 'Consent_add',
        method: 'POST',
        path: '/consent',
        action: 'consent:record',
        auth: 'tenant',
        inputSchema: {
            type: 'object',
            properties: {
                body: {
                    type: 'object',
                    required: ['subject', 'channel', 'status'],
                    properties: {
                        subject: {
                            type: 'string',
                        },
                        channel: {
                            type: 'string',
                            enum: ['email', 'sms', 'push'],
                            description: "`email` and `push` are delivered; `sms` is the Phase 3 seam (carrier registration per tenant).\n`push` reaches a person's registered phones with a fixed sentence and no record data.",
                        },
                        status: {
                            type: 'string',
                            enum: ['opted-in', 'opted-out'],
                            description: 'Whether a subject may be contacted on a channel, and for what.',
                        },
                        scope: {
                            type: 'string',
                            enum: ['marketing', 'all'],
                            description: '`marketing` opt-out leaves transactional mail allowed (and recorded); `all` blocks every send.',
                            default: 'marketing',
                        },
                        note: {
                            type: 'string',
                            maxLength: 500,
                        },
                    },
                },
            },
            required: ['body'],
            additionalProperties: false,
        },
    },
    {
        name: 'engagement_Devices_list',
        description: "The caller's phones registered for push notifications in this workspace.",
        context: 'engagement',
        operationId: 'Devices_list',
        method: 'GET',
        path: '/devices',
        action: 'device:list',
        auth: 'tenant',
        inputSchema: {
            type: 'object',
            properties: {
                query: {
                    type: 'object',
                    properties: {
                        cursor: {
                            type: 'string',
                        },
                        limit: {
                            type: 'integer',
                            format: 'int32',
                            minimum: 1,
                            maximum: 200,
                            default: 50,
                        },
                    },
                    required: [],
                    additionalProperties: false,
                },
            },
            required: [],
            additionalProperties: false,
        },
    },
    {
        name: 'engagement_Devices_remove',
        description: "Stop push notifications to one of the caller's phones.",
        context: 'engagement',
        operationId: 'Devices_remove',
        method: 'DELETE',
        path: '/devices/{deviceId}',
        action: 'device:remove',
        auth: 'tenant',
        inputSchema: {
            type: 'object',
            properties: {
                params: {
                    type: 'object',
                    properties: {
                        deviceId: {
                            type: 'string',
                            pattern: '^[a-z2-7]{26}$',
                            description: "A registered phone's id: derived from its push token, so the same token is the same device.",
                        },
                    },
                    required: ['deviceId'],
                    additionalProperties: false,
                },
            },
            required: ['params'],
            additionalProperties: false,
        },
    },
    {
        name: 'engagement_Notices_list',
        description: "The caller's current notices, newest first: not withdrawn, not expired; acknowledged ones included.",
        context: 'engagement',
        operationId: 'Notices_list',
        method: 'GET',
        path: '/notices',
        action: 'notice:list',
        auth: 'tenant',
        inputSchema: {
            type: 'object',
            properties: {},
            required: [],
            additionalProperties: false,
        },
    },
    {
        name: 'engagement_Notifications_list',
        description: "Messages engagement delivered (or tried to) for the caller's tenant, newest first.",
        context: 'engagement',
        operationId: 'Notifications_list',
        method: 'GET',
        path: '/notifications',
        action: 'notification:list',
        auth: 'tenant',
        inputSchema: {
            type: 'object',
            properties: {
                query: {
                    type: 'object',
                    properties: {
                        cursor: {
                            type: 'string',
                        },
                        limit: {
                            type: 'integer',
                            format: 'int32',
                            minimum: 1,
                            maximum: 200,
                            default: 50,
                        },
                        since: {
                            type: 'string',
                            format: 'date-time',
                        },
                        status: {
                            type: 'string',
                            enum: [
                                'queued',
                                'sent',
                                'delivered',
                                'bounced',
                                'complained',
                                'failed',
                                'suppressed',
                            ],
                            description: 'Lifecycle of one message. `queued` → `sent` (provider accepted) → `delivered` | `bounced` |\n`complained`; `failed` and `suppressed` are terminal without a provider round trip.',
                        },
                        template: {
                            type: 'string',
                            enum: [
                                'invitation',
                                'receipt',
                                'payment-failed',
                                'password-reset-notice',
                                'checkout-link',
                                'workspace-delayed',
                                'account-ready',
                                'onboarding-failed-alert',
                                'reminder-due',
                                'message-received',
                                'task-assigned',
                                'account-notice',
                            ],
                            description: "Templates live in engagement's code; a producer names one and supplies its data.",
                        },
                    },
                    required: [],
                    additionalProperties: false,
                },
            },
            required: [],
            additionalProperties: false,
        },
    },
    {
        name: 'engagement_Notifications_get',
        description: 'One message and its delivery state.',
        context: 'engagement',
        operationId: 'Notifications_get',
        method: 'GET',
        path: '/notifications/{notificationId}',
        action: 'notification:read',
        auth: 'tenant',
        inputSchema: {
            type: 'object',
            properties: {
                params: {
                    type: 'object',
                    properties: {
                        notificationId: {
                            type: 'string',
                            pattern: '^[0-9A-HJKMNP-TV-Z]{26}$',
                            description: 'ULID-style opaque identifier for records.',
                        },
                    },
                    required: ['notificationId'],
                    additionalProperties: false,
                },
            },
            required: ['params'],
            additionalProperties: false,
        },
    },
    {
        name: 'engagement_Preferences_get',
        description: "The caller's notification preferences in this workspace.",
        context: 'engagement',
        operationId: 'Preferences_get',
        method: 'GET',
        path: '/preferences',
        action: 'preference:read',
        auth: 'tenant',
        inputSchema: {
            type: 'object',
            properties: {},
            required: [],
            additionalProperties: false,
        },
    },
    {
        name: 'engagement_Preferences_update',
        description: 'Turn push notifications on or off for the caller in this workspace.',
        context: 'engagement',
        operationId: 'Preferences_update',
        method: 'PUT',
        path: '/preferences',
        action: 'preference:update',
        auth: 'tenant',
        inputSchema: {
            type: 'object',
            properties: {
                body: {
                    type: 'object',
                    required: ['push'],
                    properties: {
                        push: {
                            type: 'boolean',
                        },
                    },
                },
            },
            required: ['body'],
            additionalProperties: false,
        },
    },
];
//# sourceMappingURL=tools.js.map